/**
 * ProseMirrorPortal: Creates the React portal and renders the editor UI
 *
 * Why use a portal?
 * - ProseMirror manages its own DOM directly (not through React)
 * - React's reconciliation would conflict with ProseMirror's DOM management
 * - The portal isolates ProseMirror's DOM from React's reconciliation
 */

import clsx from "clsx";
import { get } from "lodash-es";
import Delta from "quill-delta";
import { FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { ProseMirrorEditorInstance } from "./prosemirror/editor-instance";
import type { ProseMirrorEditorInstanceOptions, ProseMirrorPortalProps } from "./prosemirror/types";

import { USER_INPUT_EVENT_CATEGORY, addTrackingToEvent, getPageFieldProps } from "utils/amplitude";

/**
 * ProseMirrorPortal renders the editor UI through a React portal
 *
 * This component:
 * 1. Uses the container element provided by the parent component
 * 2. Creates a ProseMirrorEditorInstance and attaches it to a div inside the portal
 * 3. Recreates the editor instance when readOnly changes (since editable is set at init)
 * 4. Cleans up the editor instance on unmount
 */
export const ProseMirrorPortal: FC<ProseMirrorPortalProps> = ({
  containerElement,
  className = "",
  readOnly,
  doc,
  path,
  userId,
  presenceViewType,
  users,
  eventName,
  pageId,
  embedded = false,
  create = false,
  showPlaceholder = false,
  placeHolderText = "",
}) => {
  const rootElementRef = useRef<HTMLDivElement | null>(null);
  const editorInstanceRef = useRef<ProseMirrorEditorInstance | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const hasContainer = containerElement !== null;

  useEffect(() => {
    if (!containerElement || !rootElementRef.current) return;

    const options: ProseMirrorEditorInstanceOptions = {
      doc,
      path,
      readOnly,
      userId,
      presenceViewType,
      users,
      embedded,
      create,
      pageId,
      showPlaceholder,
      placeHolderText,
    };
    const instance = new ProseMirrorEditorInstance(rootElementRef.current, options);
    editorInstanceRef.current = instance;

    const editorElement = rootElementRef.current;
    const handleFocus = addTrackingToEvent({
      eventName: eventName || "Free response block selected",
      eventProperties: { ...getPageFieldProps(pageId, path), ...USER_INPUT_EVENT_CATEGORY },
      onEvent: () => setIsFocused(true),
    });
    const handleBlur = () => setIsFocused(false);
    editorElement.addEventListener("focusin", handleFocus);
    editorElement.addEventListener("focusout", handleBlur);

    return () => {
      editorElement.removeEventListener("focusin", handleFocus);
      editorElement.removeEventListener("focusout", handleBlur);
      instance.destroy();
      editorInstanceRef.current = null;
    };
    // We should take care to avoid recreating the editor unnecessarily.
    // Dependencies not included: containerElement, doc, path, userId, users, pageId,
    // eventName, className, embedded, create, placeHolderText.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasContainer, readOnly, presenceViewType, showPlaceholder]);

  // Timeline view replaces doc between renders, so we pull the new content here
  // Also, ShareDB's automatic error recovery will re-fetch the doc data entirely on error,
  // which this file needs to react to by detecting changes in doc.data.
  useEffect(() => {
    if (editorInstanceRef.current) {
      const data = get(doc.data, path) as Delta;
      editorInstanceRef.current.syncContentsFromDoc(data, path);
    }
  }, [doc, path, doc?.data]);

  if (!containerElement) {
    return null;
  }

  return createPortal(
    <div
      className={clsx("prosemirror-editor-wrapper", className, {
        focused: isFocused,
        "textfield-disabled": readOnly,
        embedded,
      })}
    >
      {/*
        This div is where ProseMirror EditorView will attach.
        The editor content is rendered directly by ProseMirror into this element.
        Because this is rendered via a portal, React won't try to reconcile
        the DOM that ProseMirror manages.
      */}
      <div className="prosemirror-editor" ref={rootElementRef} />
    </div>,
    containerElement,
  );
};
